/**
 * RussianHyphenator
 *
 * Кратко:
 * Плагин типографики для русского текста:
 * 1) добавляет мягкие переносы (&shy;) внутри слов;
 * 2) по опции связывает короткие предлоги/союзы с последующим словом через &nbsp;;
 * 3) связывает типовые части адреса (дом/корпус/кв. + номер), чтобы они не разрывались;
 * 4) связывает число и единицу измерения (кг, м, %, °C, км/ч, м² и т.п.);
 * 5) связывает число и следующее слово (например: "5 лет", "2024 год");
 * 6) по опции конвертирует кавычки в нужный стиль;
 * 7) по опции применяет пользовательские правила поиска/замены.
 *
 * Полное руководство:
 *
 * import RussianHyphenator from './hyphenate';
 *
 * const hyphenator = new RussianHyphenator({
 *   // Не переносить слова длиной <= N.
 *   // По умолчанию: 3
 *   minWordLength: 3,
 *
 *   // Минимальная длина первого фрагмента до первого переноса.
 *   // Если найдены точки переноса левее этого порога, они отбрасываются.
 *   // По умолчанию: 4
 *   minLeadingFragmentLength: 4,
 *
 *   // Включает фильтр minLeadingFragmentLength.
 *   // По умолчанию: true
 *   // (оставлено для обратной совместимости)
 *   skipWordIfLeadingFragmentTooShort: true,
 *
 *   // Запрещенные длины фрагментов (слева или справа от точки переноса).
 *   // Пример: [2, 3] — не разрешать куски длиной 2 или 3.
 *   // По умолчанию: []
 *   forbiddenFragmentLengths: [],
 *
 *   // Если true и найден хотя бы один запрещенный вариант переноса —
 *   // слово не переносится целиком.
 *   // Если false — запрещенные точки отбрасываются, остальные остаются.
 *   // По умолчанию: false
 *   skipWordIfHasForbiddenFragments: false,
 *
 *   // Склеивать предлоги/союзы со следующим словом через &nbsp;.
 *   // По умолчанию: true
 *   bindPrepositionsAndConjunctions: true,
 *
 *   // Склеивать адресные части с номерами через &nbsp;:
 *   // "Либкнехта 14, корпус 1" -> "Либкнехта&nbsp;14, корпус&nbsp;1"
 *   // По умолчанию: всегда включено
 *
 *   // Склеивать число и единицу измерения через &nbsp;:
 *   // "15 кг", "20 м²", "90 %", "25 °C", "60 км/ч"
 *   // По умолчанию: всегда включено
 *
 *   // Склеивать число и следующее слово через &nbsp;:
 *   // "5 лет", "2024 год", "3 квартиры"
 *   // По умолчанию: всегда включено
 *
 *   // Конвертация кавычек:
 *   // null / undefined - без конвертации (по умолчанию)
 *   // 'guillemets'      - « ... »
 *   // 'lowHigh'         - „ ... “
 *   // 'straight'        - " ... " (обратное преобразование к прямым)
 *   quoteConversionStyle: null,
 *
 *   // Совместимость со старой опцией:
 *   // convertQuotesToGuillemets: true -> эквивалент quoteConversionStyle: 'guillemets'
 *   convertQuotesToGuillemets: false,
 *
 *   // Пользовательские правила поиска/замены.
 *   // Формат 1 (словарь):
 *   // customReplacements: { '...': '…', '--': '—' }
 *   //
 *   // Формат 2 (массив правил):
 *   // customReplacements: [
 *   //   { search: '[ ]{2,}', replace: ' ', useRegex: true, flags: 'g' },
 *   //   { search: '\"', replace: '«' }
 *   // ]
 *   customReplacements: null
 * });
 *
 * // Опции по умолчанию (единая константа):
 * // import RussianHyphenator, { HYPHENATOR_DEFAULT_OPTIONS } from './hyphenate';
 * // console.log(HYPHENATOR_DEFAULT_OPTIONS);
 *
 * // Передача опций через data-* атрибуты элемента:
 * // data-hyphenator-min-word-length="3"
 * // data-hyphenator-min-leading-fragment-length="4"
 * // data-hyphenator-skip-word-if-leading-fragment-too-short="true"
 * // data-hyphenator-forbidden-fragment-lengths="2,3"
 * // data-hyphenator-skip-word-if-has-forbidden-fragments="false"
 * // data-hyphenator-bind-prepositions-and-conjunctions="true"
 * // data-hyphenator-quote-conversion-style="guillemets"
 * // data-hyphenator-convert-quotes-to-guillemets="false"
 * // data-hyphenator-custom-replacements-json='{"--":"—"}'
 *
 * // Обработка полного текста:
 * const output = hyphenator.processText(inputText);
 *
 * // Обработка одного слова:
 * const hyphenatedWord = hyphenator.hyphenateWord('телескопические');
 *
 * // Безопасная обработка DOM-элемента (с сохранением вложенного HTML):
 * // hyphenator.processElement(document.querySelector('[data-hyphenator]'));
 */
export const HYPHENATOR_DEFAULT_OPTIONS = Object.freeze({
	// Не переносить слова длиной <= N.
	minWordLength: 3,
	// Минимальная длина первого фрагмента до первого переноса.
	minLeadingFragmentLength: 4,
	// Включает фильтр minLeadingFragmentLength.
	skipWordIfLeadingFragmentTooShort: true,
	// Запрещенные длины фрагментов (слева или справа от точки переноса).
	forbiddenFragmentLengths: [],
	// Если true и найден хотя бы один запрещенный вариант переноса — слово не переносится целиком.
	skipWordIfHasForbiddenFragments: false,
	// Склеивать предлоги/союзы со следующим словом через &nbsp;.
	bindPrepositionsAndConjunctions: true,
	// null | 'guillemets' | 'lowHigh' | 'straight'
	quoteConversionStyle: null,
	// Совместимость со старой опцией.
	convertQuotesToGuillemets: false,
	// Пользовательские правила поиска/замены.
	customReplacements: null
});

export const HYPHENATOR_DATA_ATTRIBUTES = Object.freeze({
	minWordLength: 'data-hyphenator-min-word-length',
	minLeadingFragmentLength: 'data-hyphenator-min-leading-fragment-length',
	skipWordIfLeadingFragmentTooShort: 'data-hyphenator-skip-word-if-leading-fragment-too-short',
	forbiddenFragmentLengths: 'data-hyphenator-forbidden-fragment-lengths',
	skipWordIfHasForbiddenFragments: 'data-hyphenator-skip-word-if-has-forbidden-fragments',
	bindPrepositionsAndConjunctions: 'data-hyphenator-bind-prepositions-and-conjunctions',
	quoteConversionStyle: 'data-hyphenator-quote-conversion-style',
	convertQuotesToGuillemets: 'data-hyphenator-convert-quotes-to-guillemets',
	customReplacements: 'data-hyphenator-custom-replacements-json'
});

class RussianHyphenator {
	constructor(options = {}) {
		const resolvedOptions = { ...HYPHENATOR_DEFAULT_OPTIONS, ...options };
		this.vowels = new Set('аеёиоуыэюя');
		this.unbreakablePairs = new Set(['чк', 'чн', 'чт', 'щн', 'нч', 'рщ', 'рч', 'лч', 'нщ']);
		this.signs = new Set(['ь', 'ъ', 'й']);
		this.minWordLength = Number.isInteger(resolvedOptions.minWordLength) ? resolvedOptions.minWordLength : HYPHENATOR_DEFAULT_OPTIONS.minWordLength;
		this.forbiddenFragmentLengths = new Set(
			Array.isArray(resolvedOptions.forbiddenFragmentLengths) ? resolvedOptions.forbiddenFragmentLengths : HYPHENATOR_DEFAULT_OPTIONS.forbiddenFragmentLengths
		);
		this.skipWordIfHasForbiddenFragments = Boolean(resolvedOptions.skipWordIfHasForbiddenFragments);
		this.minLeadingFragmentLength = Number.isInteger(resolvedOptions.minLeadingFragmentLength)
			? resolvedOptions.minLeadingFragmentLength
			: HYPHENATOR_DEFAULT_OPTIONS.minLeadingFragmentLength;
		this.skipWordIfLeadingFragmentTooShort = resolvedOptions.skipWordIfLeadingFragmentTooShort !== false;
		this.bindPrepositionsAndConjunctions = resolvedOptions.bindPrepositionsAndConjunctions !== false;
		this.quoteConversionStyle = resolvedOptions.quoteConversionStyle || null;
		if (!this.quoteConversionStyle && resolvedOptions.convertQuotesToGuillemets) {
			this.quoteConversionStyle = 'guillemets';
		}
		this.customReplacements = resolvedOptions.customReplacements || null;
		this.quotePairs = {
			guillemets: { open: '«', close: '»' },
			lowHigh: { open: '„', close: '“' }
		};
		this.serviceWords = new Set([
			'а', 'и', 'или', 'либо', 'но', 'да', 'же', 'ли', 'бы',
			'в', 'во', 'к', 'ко', 'с', 'со', 'у', 'о', 'об', 'обо',
			'от', 'до', 'по', 'из', 'за', 'на', 'под', 'над', 'при',
			'про', 'без', 'для', 'через', 'перед'
		]);
		this.excludedTextParentTags = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'CODE', 'PRE']);
		this.wordRegex = /[А-Яа-яЁё]+/g;
		this.serviceWordsRegex = /(^|[\s\u00A0])([А-Яа-яЁё]{1,5})(\s+)(?=[А-Яа-яЁё])/g;
		this.parenthesizedAbbreviationRegex = /(\S)\s+\(([A-Za-zА-Яа-яЁё.%°\/²³]{1,6})\)/g;
		this.quoteCharsRegex = /["«»„“]/g;
		this.numberWithWordRegex = /(\d+(?:[.,]\d+)?)\s+([А-Яа-яЁёA-Za-z]{1,30})(?=$|[\s,.;:!?\)])/g;

		const addressPartPattern = '(?:дом|д\\.|корпус|корп\\.?|к\\.|строение|стр\\.?|литер[а]?|лит\\.?|подъезд|кв\\.?|квартира)';
		this.addressPartWithNumberRegex = new RegExp(`\\b(${addressPartPattern})\\s+(\\d+[А-Яа-яЁёA-Za-z\\/-]*)`, 'gi');
		this.streetNameWithNumberRegex = new RegExp(
			`([А-Яа-яЁё])\\s+(\\d+[А-Яа-яЁёA-Za-z\\/-]*)(?=(?:\\s*,\\s*${addressPartPattern}\\b|(?:\\s*,)?\\s*$))`,
			'g'
		);

		const numberPattern = '(\\d+(?:[.,]\\d+)?)';
		const unitPattern = '(?:%|‰|°[CFС]?|кг|г|мг|т|м|см|мм|км|м²|м³|м2|м3|л|мл|ч|мин|с|км\\/ч|м\\/с)';
		this.numberWithUnitRegex = new RegExp(
			`${numberPattern}(?:\\s+|&nbsp;)?(${unitPattern})(?=$|[\\s,.;:!?\\)])`,
			'gi'
		);
	}

	static toBoolean(value) {
		if (typeof value === 'boolean') return value;
		if (typeof value !== 'string') return undefined;
		const normalized = value.trim().toLowerCase();
		if (normalized === 'true' || normalized === '1') return true;
		if (normalized === 'false' || normalized === '0') return false;
		return undefined;
	}

	static toInteger(value) {
		if (typeof value !== 'string' || value.trim() === '') return undefined;
		const parsed = Number.parseInt(value, 10);
		return Number.isNaN(parsed) ? undefined : parsed;
	}

	static toIntegerArray(value) {
		if (typeof value !== 'string' || value.trim() === '') return undefined;
		const values = value
			.split(',')
			.map(part => Number.parseInt(part.trim(), 10))
			.filter(num => Number.isInteger(num));
		return values.length ? values : [];
	}

	static toJson(value) {
		if (typeof value !== 'string' || value.trim() === '') return undefined;
		try {
			return JSON.parse(value);
		} catch (error) {
			return undefined;
		}
	}

	static optionsFromDataset(dataset = {}) {
		const options = {};
		const minWordLength = this.toInteger(dataset.hyphenatorMinWordLength);
		if (minWordLength !== undefined) options.minWordLength = minWordLength;

		const minLeadingFragmentLength = this.toInteger(dataset.hyphenatorMinLeadingFragmentLength);
		if (minLeadingFragmentLength !== undefined) options.minLeadingFragmentLength = minLeadingFragmentLength;

		const skipWordIfLeadingFragmentTooShort = this.toBoolean(dataset.hyphenatorSkipWordIfLeadingFragmentTooShort);
		if (skipWordIfLeadingFragmentTooShort !== undefined) {
			options.skipWordIfLeadingFragmentTooShort = skipWordIfLeadingFragmentTooShort;
		}

		const forbiddenFragmentLengths = this.toIntegerArray(dataset.hyphenatorForbiddenFragmentLengths);
		if (forbiddenFragmentLengths !== undefined) options.forbiddenFragmentLengths = forbiddenFragmentLengths;

		const skipWordIfHasForbiddenFragments = this.toBoolean(dataset.hyphenatorSkipWordIfHasForbiddenFragments);
		if (skipWordIfHasForbiddenFragments !== undefined) {
			options.skipWordIfHasForbiddenFragments = skipWordIfHasForbiddenFragments;
		}

		const bindPrepositionsAndConjunctions = this.toBoolean(dataset.hyphenatorBindPrepositionsAndConjunctions);
		if (bindPrepositionsAndConjunctions !== undefined) {
			options.bindPrepositionsAndConjunctions = bindPrepositionsAndConjunctions;
		}

		const quoteConversionStyle = typeof dataset.hyphenatorQuoteConversionStyle === 'string'
			? dataset.hyphenatorQuoteConversionStyle.trim()
			: '';
		if (quoteConversionStyle) options.quoteConversionStyle = quoteConversionStyle;

		const convertQuotesToGuillemets = this.toBoolean(dataset.hyphenatorConvertQuotesToGuillemets);
		if (convertQuotesToGuillemets !== undefined) {
			options.convertQuotesToGuillemets = convertQuotesToGuillemets;
		}

		const customReplacements = this.toJson(dataset.hyphenatorCustomReplacementsJson);
		if (customReplacements !== undefined) options.customReplacements = customReplacements;

		return options;
	}

	static optionsFromElement(element) {
		if (!element || !element.dataset) return {};
		return this.optionsFromDataset(element.dataset);
	}

	isVowel(char) {
		return this.vowels.has(char.toLowerCase());
	}

	hasVowel(chars, from, to) {
		for (let i = from; i <= to; i++) {
			if (this.isVowel(chars[i])) return true;
		}

		return false;
	}

	canBreakAt(chars, i) {
		const leftLength = i + 1;
		const rightLength = chars.length - leftLength;

		// Не оставляем в строке 1 букву слева/справа.
		if (leftLength < 2 || rightLength < 2) return false;

		const left = chars[i].toLowerCase();
		const right = chars[i + 1].toLowerCase();
		const next = chars[i + 2] ? chars[i + 2].toLowerCase() : '';
		const prev = chars[i - 1] ? chars[i - 1].toLowerCase() : '';

		// В правой части должна оставаться гласная.
		if (!this.hasVowel(chars, i + 1, chars.length - 1)) return false;

		// В левой части гласная обязательна, кроме случаев типа "съ-езд".
		if (!this.hasVowel(chars, 0, i)) {
			if (!(this.signs.has(left) && this.isVowel(right))) return false;
		}

		// Ь/Ъ/Й не отрываются от предыдущей буквы.
		if (this.signs.has(right)) return false;

		// Не разрываем устойчивые сочетания согласных.
		if (!this.isVowel(left) && !this.isVowel(right)) {
			const pair = left + right;
			if (this.unbreakablePairs.has(pair)) return false;
		}

		// Не переносим перед группой CЪV (например, "по-дъезд" недопустимо).
		if (!this.isVowel(right) && next === 'ъ') return false;

		// Граница между двумя гласными: по-езд.
		if (this.isVowel(left) && this.isVowel(right)) return true;

		// После гласной перед согласной только если далее начинается новый слог:
		// V-CV или V-CЬ/Ъ.
		if (this.isVowel(left) && !this.isVowel(right)) {
			return this.isVowel(next) || next === 'ь' || next === 'ъ';
		}

		// После ь/ъ/й перед согласной: май-ка, боль-ше, подъ-езд.
		if (this.signs.has(left) && !this.isVowel(right)) return true;

		// После ь/ъ/й перед гласной: съ-езд, объ-ект.
		if (this.signs.has(left) && this.isVowel(right)) return true;

		// Между согласными при стечении перед гласной: сол-нце, кас-са.
		if (!this.isVowel(left) && !this.isVowel(right) && this.isVowel(next)) return true;

		// После согласной перед гласной переносим только после ь/ъ/й.
		if (!this.isVowel(left) && this.isVowel(right) && this.signs.has(prev)) return true;

		return false;
	}

	hyphenateWord(word) {
		// Короткие слова и аббревиатуры не переносим.
		if (word.length <= this.minWordLength || word.toUpperCase() === word) return word;

		const chars = Array.from(word);
		const positions = [];
		for (let i = 0; i < chars.length - 1; i++) {
			if (this.canBreakAt(chars, i)) {
				positions.push(i);
			}
		}

		if (!positions.length) return word;

		const sortedUnique = Array.from(new Set(positions)).sort((a, b) => a - b);

		if (this.minLeadingFragmentLength > 0 && this.skipWordIfLeadingFragmentTooShort) {
			const filteredLeading = sortedUnique.filter(pos => (pos + 1) >= this.minLeadingFragmentLength);
			if (!filteredLeading.length) return word;
			sortedUnique.length = 0;
			sortedUnique.push(...filteredLeading);
		}

		if (this.forbiddenFragmentLengths.size) {
			const hasForbiddenFragments = sortedUnique.some(pos => {
				const left = pos + 1;
				const right = chars.length - left;
				return this.forbiddenFragmentLengths.has(left) || this.forbiddenFragmentLengths.has(right);
			});

			if (hasForbiddenFragments && this.skipWordIfHasForbiddenFragments) {
				return word;
			}

			const filtered = sortedUnique.filter(pos => {
				const left = pos + 1;
				const right = chars.length - left;
				return !this.forbiddenFragmentLengths.has(left) && !this.forbiddenFragmentLengths.has(right);
			});

			if (!filtered.length) return word;
			sortedUnique.length = 0;
			sortedUnique.push(...filtered);
		}

		// Вставляем &shy; в найденные позиции (с конца, чтобы индексы не сбивались)
		let result = word;
		for (let j = sortedUnique.length - 1; j >= 0; j--) {
			const pos = sortedUnique[j];
			result = result.slice(0, pos + 1) + '&shy;' + result.slice(pos + 1);
		}

		return result;
	}

	bindServiceWords(text) {
		return text.replace(
			this.serviceWordsRegex,
			(match, prefix, word) => (
				this.serviceWords.has(word.toLowerCase())
					? `${prefix}${word}&nbsp;`
					: match
			)
		);
	}

	bindParenthesizedAbbreviations(text) {
		return text.replace(
			this.parenthesizedAbbreviationRegex,
			'$1&nbsp;($2)'
		);
	}

	bindAddressParts(text) {
		const withBoundAddressParts = text.replace(
			this.addressPartWithNumberRegex,
			'$1&nbsp;$2'
		);

		return withBoundAddressParts.replace(
			this.streetNameWithNumberRegex,
			'$1&nbsp;$2'
		);
	}

	bindNumbersWithUnits(text) {
		return text.replace(
			this.numberWithUnitRegex,
			'$1&nbsp;$2'
		);
	}

	bindNumbersWithWords(text) {
		return text.replace(this.numberWithWordRegex, '$1&nbsp;$2');
	}

	convertQuotes(text) {
		if (!this.quoteConversionStyle) return text;

		// Обратное преобразование к прямым кавычкам.
		if (this.quoteConversionStyle === 'straight') {
			return text.replace(this.quoteCharsRegex, '"');
		}

		const pair = this.quotePairs[this.quoteConversionStyle];
		if (!pair) return text;

		let isOpen = true;
		return text.replace(this.quoteCharsRegex, () => {
			const quote = isOpen ? pair.open : pair.close;
			isOpen = !isOpen;
			return quote;
		});
	}

	applyTextTransforms(text, transforms) {
		return transforms.reduce((result, transform) => transform(result), text);
	}

	applyCustomReplacements(text) {
		if (!this.customReplacements) return text;

		// Объект вида { "—": "-", "...": "…" }.
		if (!Array.isArray(this.customReplacements) && typeof this.customReplacements === 'object') {
			let result = text;
			for (const [search, replace] of Object.entries(this.customReplacements)) {
				result = result.split(search).join(String(replace));
			}
			return result;
		}

		// Массив правил:
		// [{ search: '...', replace: '…' }, { search: '[ ]{2,}', replace: ' ', useRegex: true, flags: 'g' }]
		if (Array.isArray(this.customReplacements)) {
			let result = text;
			for (const rule of this.customReplacements) {
				if (!rule || typeof rule.search !== 'string') continue;
				const replace = String(rule.replace ?? '');
				if (rule.useRegex) {
					const flags = typeof rule.flags === 'string' ? rule.flags : 'g';
					result = result.replace(new RegExp(rule.search, flags), replace);
					continue;
				}
				result = result.split(rule.search).join(replace);
			}
			return result;
		}

		return text;
	}

	processText(text) {
		const transforms = [
			value => this.applyCustomReplacements(value),
			value => value.replace(this.wordRegex, word => this.hyphenateWord(word)),
			value => (this.bindPrepositionsAndConjunctions ? this.bindServiceWords(value) : value),
			value => this.bindParenthesizedAbbreviations(value),
			value => this.bindAddressParts(value),
			value => this.bindNumbersWithUnits(value),
			value => this.bindNumbersWithWords(value),
			value => this.convertQuotes(value)
		];

		return this.applyTextTransforms(text, transforms);
	}

	processElement(element) {
		if (!element || typeof element !== 'object') return;

		const excludedTags = this.excludedTextParentTags;
		const walker = document.createTreeWalker(
			element,
			NodeFilter.SHOW_TEXT,
			{
				acceptNode(node) {
					if (!node.nodeValue || !node.nodeValue.trim()) {
						return NodeFilter.FILTER_SKIP;
					}

					const parent = node.parentElement;
					if (!parent || excludedTags.has(parent.tagName)) {
						return NodeFilter.FILTER_REJECT;
					}

					return NodeFilter.FILTER_ACCEPT;
				}
			}
		);
		const textNodes = this.collectTextNodes(walker);

		textNodes.forEach((textNode) => {
			const source = textNode.nodeValue;
			const processed = this.processText(source);

			if (processed === source) return;

			this.replaceTextNodeWithHtml(textNode, processed);
		});
	}

	collectTextNodes(walker) {
		const textNodes = [];
		let currentNode;

		while ((currentNode = walker.nextNode())) {
			textNodes.push(currentNode);
		}

		return textNodes;
	}

	replaceTextNodeWithHtml(textNode, html) {
		const template = document.createElement('template');
		template.innerHTML = html;
		textNode.replaceWith(template.content.cloneNode(true));
	}
}

export default RussianHyphenator;
